import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, BookOpen, Video, MapPin, Users, Megaphone,
  Settings, Code2, FileText, LogOut, Menu, X, Shield, ChevronRight,
  ToggleRight, UserCheck, Calendar, Heart, Phone, BookMarked,
  Share2, Image
} from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/',             icon: LayoutDashboard, label: 'Dashboard',      perm: null },
    ]
  },
  {
    label: 'Bot Menu Features',
    items: [
      { to: '/menu-control', icon: ToggleRight,     label: 'Menu On/Off',    perm: 'settings' },
      { to: '/join-team',    icon: UserCheck,       label: 'Join Team',      perm: null },
      { to: '/program',      icon: Calendar,        label: 'Program Info',   perm: null },
      { to: '/donate',       icon: Heart,           label: 'Donate Madrasa', perm: null },
      { to: '/contact',      icon: Phone,           label: 'Contact Us',     perm: null },
      { to: '/masail',       icon: BookMarked,      label: 'Masail',         perm: 'settings' },
      { to: '/social',       icon: Share2,          label: 'Social Media',   perm: 'settings' },
    ]
  },
  {
    label: 'Content',
    items: [
      { to: '/media',        icon: Image,           label: 'Media Send',     perm: 'settings' },
      { to: '/books',        icon: BookOpen,        label: 'Kitabein',       perm: 'books' },
      { to: '/videos',       icon: Video,           label: 'Videos',         perm: 'videos' },
      { to: '/cities',       icon: MapPin,          label: 'Cities/Groups',  perm: 'cities' },
    ]
  },
  {
    label: 'Management',
    items: [
      { to: '/users',        icon: Users,           label: 'Users',          perm: 'users' },
      { to: '/broadcast',    icon: Megaphone,       label: 'Broadcast',      perm: 'broadcast' },
      { to: '/admins',       icon: Shield,          label: 'Admin Users',    perm: 'admins' },
      { to: '/settings',     icon: Settings,        label: 'Settings',       perm: 'settings' },
      { to: '/codegen',      icon: Code2,           label: 'Code Generator', perm: null },
      { to: '/logs',         icon: FileText,        label: 'Logs',           perm: 'logs' },
    ]
  }
];

export default function Sidebar() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function doLogout() { logout(); navigate('/login'); }

  const SidebarContent = () => (
    <div style={{
      width: 'var(--sidebar-w)', height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-surface)', borderRight: '1px solid var(--border)',
      position: 'fixed', top: 0, left: 0, zIndex: 100, overflowY: 'auto'
    }}>
      {/* Logo */}
      <div style={{
        padding: '20px 18px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: 'linear-gradient(135deg, var(--emerald-800), var(--emerald-900))',
          border: '1px solid var(--emerald-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0
        }}>☪️</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--gold-300)', lineHeight: 1.2 }}>HRN Admin</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>Panel v2.0</div>
        </div>
        <button onClick={() => setOpen(false)} style={{
          marginLeft: 'auto', background: 'none', border: 'none',
          color: 'var(--text-muted)', cursor: 'pointer', display: 'none'
        }} className="mobile-close">
          <X size={16} />
        </button>
      </div>

      {/* User info */}
      <div style={{
        padding: '12px 18px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'var(--emerald-800)', border: '1px solid var(--emerald-600)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, color: 'var(--emerald-300)', fontWeight: 600, flexShrink: 0
        }}>
          {user?.username?.[0]?.toUpperCase()}
        </div>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.username}</div>
          <div style={{ fontSize: 10, color: 'var(--gold-400)', textTransform: 'capitalize' }}>{user?.role?.replace('_', ' ')}</div>
        </div>
      </div>

      {/* Nav Groups */}
      <nav style={{ flex: 1, padding: '10px 10px' }}>
        {NAV_GROUPS.map(group => {
          const visibleItems = group.items.filter(({ perm }) => !perm || can(perm) || user?.role === 'super_admin');
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label} style={{ marginBottom: 6 }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
                padding: '6px 12px 4px', letterSpacing: '0.08em',
                textTransform: 'uppercase'
              }}>
                {group.label}
              </div>
              {visibleItems.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to} to={to} end={to === '/'}
                  onClick={() => setOpen(false)}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 8, marginBottom: 1,
                    color: isActive ? 'var(--emerald-200)' : 'var(--text-secondary)',
                    background: isActive ? 'var(--emerald-900)' : 'transparent',
                    border: isActive ? '1px solid var(--emerald-700)' : '1px solid transparent',
                    fontSize: 13, fontWeight: isActive ? 500 : 400,
                    textDecoration: 'none', transition: 'all 0.15s'
                  })}
                >
                  <Icon size={14} />
                  <span style={{ flex: 1 }}>{label}</span>
                  <ChevronRight size={11} style={{ opacity: 0.3 }} />
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Logout */}
      <div style={{ padding: '12px 10px', borderTop: '1px solid var(--border)' }}>
        <button onClick={doLogout} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', gap: 10 }}>
          <LogOut size={14} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', top: 14, left: 14, zIndex: 200,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 8, cursor: 'pointer',
          color: 'var(--text-primary)', display: 'none'
        }}
        className="hamburger"
      >
        <Menu size={16} />
      </button>

      <div className="sidebar-desktop"><SidebarContent /></div>

      {open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99, backdropFilter: 'blur(2px)' }}
          onClick={() => setOpen(false)}
        />
      )}
      {open && (
        <div className="sidebar-mobile" style={{ position: 'fixed', inset: 0, zIndex: 100, pointerEvents: 'none' }}>
          <div style={{ pointerEvents: 'all', height: '100%', width: 'fit-content' }}><SidebarContent /></div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .sidebar-desktop { display: none !important; }
          .hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .sidebar-mobile { display: none; }
          .hamburger { display: none !important; }
        }
        nav a:hover { background: var(--bg-card-hover) !important; color: var(--text-primary) !important; }
      `}</style>
    </>
  );
}